/**
 * LandingPageView Component - PadelPulse Stitch Design
 * Landing/Home page with hero section, featured league, and recent results
 * High-fidelity Stitch implementation with placeholder sections
 */

import type { FeaturedLeague, League, Match } from "../types/index";
import { API_BASE, apiService } from "../services/api";
import { escapeHtml, renderLoadingState } from "../utils/ui";
import { t } from "../i18n/config";
import { renderPublicHeader, renderPublicFooter, setupPublicChrome } from "./publicChrome";

export class LandingPageView {
  private container: HTMLElement;
  private leagues: League[] = [];
  private featuredLeague: FeaturedLeague | null = null;
  private _recentMatches: Match[] = [];

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = this.getLoadingHTML();
    await this.loadData();
    this.renderLandingPage();
    this.setupEventListeners();
  }

  private getLoadingHTML(): string {
    return renderLoadingState();
  }

  private async loadData(): Promise<void> {
    try {
      const leaguesResponse = await fetch(`${API_BASE}/leagues`);
      if (leaguesResponse.ok) {
        this.leagues = await leaguesResponse.json();
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }

    // The landing page is public, so it pulls the featured league from the
    // dedicated no-auth endpoint (not the auth-gated /api/leagues above).
    try {
      const featuredResponse = await fetch(`${API_BASE}/public/leagues/featured`);
      if (featuredResponse.ok) {
        const data = await featuredResponse.json();
        this.featuredLeague = data?.featured ?? null;
      }
    } catch (error) {
      console.error('Failed to load featured league:', error);
    }
  }

  private renderLandingPage(): void {
    this.container.innerHTML = `
      <div class="min-h-screen bg-background-light dark:bg-background-dark">
        <!-- Shared public brand header (brand, Pricing nav, language, login) -->
        ${renderPublicHeader("landing")}

        <!-- Hero Section -->
        <section class="relative overflow-hidden py-16 lg:py-24">
          <!-- Gradient Background -->
          <div class="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10 dark:from-primary/5 dark:via-transparent dark:to-accent/5"></div>
          
          <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <!-- Hero Content -->
              <div class="space-y-8">
                <div class="space-y-4">
                  <div class="inline-block">
                    <span class="badge badge-primary">${t("landing.badge")}</span>
                  </div>
                  <h1 class="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 dark:text-white leading-tight">
                    ${t("landing.hero.titleLead")} <span class="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">${t("landing.hero.titleAccent")}</span>
                  </h1>
                  <p class="text-lg md:text-xl text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft max-w-xl">
                    ${t("landing.hero.subtitle")}
                  </p>
                </div>

                <!-- CTA Buttons -->
                <div class="flex flex-col sm:flex-row gap-4 pt-4">
                  <button id="cta-organizer-access" class="btn-primary">
                    <span class="material-symbols-outlined">badge</span>
                    <span>${t("landing.cta.requestAccess")}</span>
                  </button>
                  <button id="cta-watch-demo" class="btn-outline">
                    <span class="material-symbols-outlined">play_circle</span>
                    <span>${t("landing.cta.watchDemo")}</span>
                  </button>
                </div>
              </div>

              <!-- Hero Image / Illustration -->
              <div class="relative">
                <div class="absolute inset-0 bg-gradient-to-r from-primary/20 to-accent/20 dark:from-primary/10 dark:to-accent/10 rounded-3xl blur-3xl"></div>
                <div class="relative bg-white dark:bg-surface-dark daylight:bg-day-raised rounded-3xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-8 shadow-2xl daylight:shadow-day-lg">
                  <div class="space-y-6">
                    <!-- Featured Stats -->
                    <div class="grid grid-cols-2 gap-4">
                      <div class="bg-primary/10 dark:bg-primary/5 rounded-xl p-4 text-center">
                        <p class="text-3xl font-black text-primary daylight:text-day-ink">12</p>
                        <p class="text-xs text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft font-medium mt-2">${t("landing.stats.activeLeagues")}</p>
                      </div>
                      <div class="bg-success/10 dark:bg-success/5 rounded-xl p-4 text-center">
                        <p class="text-3xl font-black text-success">156</p>
                        <p class="text-xs text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft font-medium mt-2">${t("landing.stats.players")}</p>
                      </div>
                    </div>

                    <!-- Featured League Card -->
                    ${this.renderFeaturedLeagueCard()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Features Section -->
        <section class="py-16 lg:py-24 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="max-w-3xl mb-12">
              <h2 class="text-3xl md:text-4xl font-black text-slate-900 dark:text-white">${t("landing.features.title")}</h2>
              <p class="text-lg text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-3">${t("landing.features.subtitle")}</p>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              <div class="card space-y-4">
                <div class="inline-flex items-center justify-center size-12 rounded-xl bg-primary/10 dark:bg-primary/5 text-primary daylight:text-day-ink">
                  <span class="material-symbols-outlined">account_tree</span>
                </div>
                <h3 class="text-xl font-bold text-slate-900 dark:text-white">${t("landing.features.card1.title")}</h3>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${t("landing.features.card1.body")}</p>
              </div>
              <div class="card space-y-4">
                <div class="inline-flex items-center justify-center size-12 rounded-xl bg-primary/10 dark:bg-primary/5 text-primary daylight:text-day-ink">
                  <span class="material-symbols-outlined">public</span>
                </div>
                <h3 class="text-xl font-bold text-slate-900 dark:text-white">${t("landing.features.card2.title")}</h3>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${t("landing.features.card2.body")}</p>
              </div>
              <div class="card space-y-4">
                <div class="inline-flex items-center justify-center size-12 rounded-xl bg-primary/10 dark:bg-primary/5 text-primary daylight:text-day-ink">
                  <span class="material-symbols-outlined">scoreboard</span>
                </div>
                <h3 class="text-xl font-bold text-slate-900 dark:text-white">${t("landing.features.card3.title")}</h3>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${t("landing.features.card3.body")}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Statistics Section -->
        <section class="py-16 lg:py-24 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
          <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div class="card text-center">
                <p class="text-4xl font-black text-primary daylight:text-day-ink">${this.leagues.length}</p>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-sm font-medium mt-2">${t("landing.stats.activeLeagues")}</p>
              </div>
              <div class="card text-center">
                <p class="text-4xl font-black text-success">${this.getTotalPlayers()}</p>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-sm font-medium mt-2">${t("landing.stats.totalPlayers")}</p>
              </div>
              <div class="card text-center">
                <p class="text-4xl font-black text-warning">${this.getTotalMatches()}</p>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-sm font-medium mt-2">${t("landing.stats.matchesPlayed")}</p>
              </div>
              <div class="card text-center">
                <p class="text-4xl font-black text-primary daylight:text-day-ink">${this.getTotalRounds()}</p>
                <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-sm font-medium mt-2">${t("landing.stats.roundsCompleted")}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- CTA Section -->
        <section class="py-16 lg:py-24 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-gradient-to-r from-primary/5 to-accent/5 dark:from-primary/5 dark:to-accent/5">
          <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 class="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white mb-6">${t("landing.finalCta.title")}</h2>
            <p class="text-xl text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-8">${t("landing.finalCta.subtitle")}</p>
            <button id="cta-organizer-access-footer" class="btn-primary">
              <span class="material-symbols-outlined">badge</span>
              <span>${t("landing.cta.requestAccess")}</span>
            </button>
          </div>
        </section>

        ${renderPublicFooter()}
      </div>
    `;
  }

  /**
   * Hero highlight card. Renders the platform-owner's featured league from real
   * data when one exists, or a neutral empty state otherwise (so the layout
   * never collapses on a fresh/unfeatured system).
   */
  private renderFeaturedLeagueCard(): string {
    const featured = this.featuredLeague;

    if (!featured) {
      return `
        <div class="bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken rounded-xl p-4 space-y-3">
          <p class="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t('landing.featured.label')}</p>
          <h3 class="text-lg font-bold text-slate-900 dark:text-white">${t('landing.featured.emptyTitle')}</h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${t('landing.featured.emptyBody')}</p>
        </div>
      `;
    }

    const name = escapeHtml(featured.name);
    const modeLabel = featured.mode === 'individual' ? t('league.mode.individual') : t('league.mode.fixedPairs');
    const formatLabel = featured.format.includes('roundrobin') ? t('league.format.roundRobin') : t('league.format.swiss');
    const playersLabel = t(
      featured.playerCount === 1 ? 'landing.featured.players_one' : 'landing.featured.players_other',
      { count: featured.playerCount }
    );

    const viewControl = featured.sharingToken
      ? `<a href="#/leagues/${encodeURIComponent(featured.sharingToken)}" class="text-primary daylight:text-day-ink hover:text-primary/80 daylight:hover:text-day-ink-soft font-bold text-sm flex items-center gap-1">
           <span>${t('landing.featured.view')}</span>
           <span class="material-symbols-outlined" style="font-size: 18px;">arrow_forward</span>
         </a>`
      : '';

    return `
      <div class="bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken rounded-xl p-4 space-y-3">
        <p class="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${t('landing.featured.label')}</p>
        <h3 class="text-lg font-bold text-slate-900 dark:text-white">${name}</h3>
        <div class="flex gap-2 flex-wrap">
          <span class="badge badge-primary">${modeLabel}</span>
          <span class="badge badge-success">${playersLabel}</span>
        </div>
        <div class="pt-3 border-t border-slate-200 dark:border-slate-700 daylight:border-day-ink/10 flex items-center justify-between gap-3">
          <span class="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${formatLabel}</span>
          ${viewControl}
        </div>
      </div>
    `;
  }

  private renderResultCards(): string {
    const placeholderResults = [
      { team1: 'Team A', team2: 'Team B', score: '2-1', league: 'Summer Championship' },
      { team1: 'Team C', team2: 'Team D', score: '2-0', league: 'Weekly Tournament' },
      { team1: 'Team E', team2: 'Team F', score: '1-2', league: 'Summer Championship' },
    ];

    return placeholderResults.map(result => `
      <div class="card hover:border-primary/50 transition-colors cursor-pointer">
        <p class="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-3">${result.league}</p>
        <div class="flex items-center justify-between mb-4">
          <div class="flex-1">
            <p class="font-semibold text-slate-900 dark:text-white">${result.team1}</p>
          </div>
          <div class="text-center px-4">
            <p class="text-2xl font-black text-primary daylight:text-day-ink">${result.score}</p>
          </div>
          <div class="flex-1 text-right">
            <p class="font-semibold text-slate-900 dark:text-white">${result.team2}</p>
          </div>
        </div>
        <div class="pt-3 border-t border-slate-200 dark:border-slate-700 daylight:border-day-ink/10">
          <button class="text-primary daylight:text-day-ink hover:text-primary/80 daylight:hover:text-day-ink-soft font-bold text-sm flex items-center gap-1 ml-auto">
            <span>View Details</span>
            <span class="material-symbols-outlined" style="font-size: 18px;">arrow_forward</span>
          </button>
        </div>
      </div>
    `).join('');
  }

  private renderLeagueCards(): string {
    return this.leagues.slice(0, 6).map(league => `
      <div class="card hover:border-primary/50 transition-colors cursor-pointer">
        <div class="flex items-start justify-between mb-4">
          <div class="flex-1">
            <h3 class="text-xl font-bold text-slate-900 dark:text-white">${league.name}</h3>
            <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-1">${league.description || 'League description'}</p>
          </div>
        </div>
        <div class="flex gap-2 mb-4">
          <span class="badge badge-primary">${league.mode === 'fixed-pairs' ? 'Fixed Pairs' : 'Individual'}</span>
          <span class="badge badge-success">Active</span>
        </div>
        <div class="flex gap-4 py-4 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 text-sm">
          <div>
            <p class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-xs font-medium">Players</p>
            <p class="font-bold text-slate-900 dark:text-white">8</p>
          </div>
          <div>
            <p class="text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted text-xs font-medium">Rounds</p>
            <p class="font-bold text-slate-900 dark:text-white">4</p>
          </div>
          <div class="flex-1 text-right">
            <button class="btn-secondary">
              <span>View</span>
              <span class="material-symbols-outlined">arrow_forward</span>
            </button>
          </div>
        </div>
      </div>
    `).join('');
  }

  private getTotalPlayers(): number {
    // Placeholder - would calculate from actual data
    return 48;
  }

  private getTotalMatches(): number {
    // Placeholder - would calculate from actual data
    return 156;
  }

  private getTotalRounds(): number {
    // Placeholder - would calculate from actual data
    return 32;
  }

  private setupEventListeners(): void {
    // Primary CTA: open the "Request Organizer Access" lead form modal. This is
    // the public intake for prospective organizers (they have no account yet);
    // the owner reviews the lead and provisions them manually. Returning
    // organizers use the header login button (routes to "#login") instead.
    const accessButtons = this.container.querySelectorAll(
      '#cta-organizer-access, #cta-organizer-access-footer'
    );
    accessButtons.forEach(btn => {
      btn.addEventListener('click', () => this.requestOrganizerAccess());
    });

    // Secondary CTA: open a self-contained demo modal with a video placeholder.
    const demoBtn = this.container.querySelector('#cta-watch-demo');
    demoBtn?.addEventListener('click', () => this.openDemoModal());

    // Shared public header controls: the login button (routes to "#login"),
    // the language toggle, and the responsive mobile-nav drawer + hamburger.
    // Switching locale re-renders this view via the locale-changed listener
    // registered in main.ts.
    setupPublicChrome(this.container);
  }

  private requestOrganizerAccess(): void {
    this.openLeadModal();
  }

  /**
   * The public "Request Organizer Access" form modal. Collects Name,
   * Organization Name, Email and Phone, plus a visually-hidden honeypot field
   * ("organization_fax") the server uses to silently drop bot submissions. On
   * success the form is replaced with a confirmation state.
   */
  private openLeadModal(): void {
    const inputClass =
      'w-full px-4 py-3 border border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 bg-white dark:bg-slate-800 daylight:bg-day-raised rounded-lg text-slate-900 dark:text-white daylight:text-day-ink focus:ring-2 focus:ring-primary focus:border-transparent transition';
    const labelClass =
      'block text-sm font-semibold text-slate-900 dark:text-white daylight:text-day-ink mb-2';

    const modal = document.createElement('div');
    modal.className =
      'fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', t('landing.lead.ariaLabel'));
    modal.innerHTML = `
      <div class="relative w-full max-w-md bg-white dark:bg-card-dark daylight:bg-day-card rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-6 sm:p-8 animate-in zoom-in-95 duration-200">
        <button id="lead-modal-close" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors" aria-label="${t('landing.lead.close')}">
          <span class="material-symbols-outlined">close</span>
        </button>
        <div id="lead-modal-body">
          <h2 class="text-2xl font-black text-slate-900 dark:text-white daylight:text-day-ink mb-1 pr-10">${t('landing.lead.title')}</h2>
          <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-6">${t('landing.lead.subtitle')}</p>
          <form id="lead-form" class="space-y-4" novalidate>
            <div>
              <label for="lead-name" class="${labelClass}">${t('landing.lead.name')}</label>
              <input type="text" id="lead-name" name="name" required autocomplete="name" class="${inputClass}" placeholder="${t('landing.lead.namePlaceholder')}" />
            </div>
            <div>
              <label for="lead-org" class="${labelClass}">${t('landing.lead.org')}</label>
              <input type="text" id="lead-org" name="organizationName" required autocomplete="organization" class="${inputClass}" placeholder="${t('landing.lead.orgPlaceholder')}" />
            </div>
            <div>
              <label for="lead-email" class="${labelClass}">${t('landing.lead.email')}</label>
              <input type="email" id="lead-email" name="email" required autocomplete="email" class="${inputClass}" placeholder="${t('landing.lead.emailPlaceholder')}" />
            </div>
            <div>
              <label for="lead-phone" class="${labelClass}">${t('landing.lead.phone')}</label>
              <input type="tel" id="lead-phone" name="phone" required autocomplete="tel" class="${inputClass}" placeholder="${t('landing.lead.phonePlaceholder')}" />
            </div>

            <!-- Honeypot: visually hidden and unreachable for humans (off-screen,
                 zero-size, aria-hidden, not tab-focusable, autocomplete off), but
                 naive bots fill it. Any value makes the server silently drop the
                 submission. -->
            <div aria-hidden="true" style="position:absolute; left:-9999px; top:auto; width:1px; height:1px; overflow:hidden;">
              <label for="organization_fax">${t('landing.lead.honeypot')}</label>
              <input type="text" id="organization_fax" name="organization_fax" tabindex="-1" autocomplete="off" />
            </div>

            <div id="lead-error" class="hidden bg-error/10 border border-error/30 text-error rounded-lg p-3 text-sm font-medium"></div>
            <button type="submit" id="lead-submit" class="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed">
              <span class="material-symbols-outlined">send</span><span>${t('landing.lead.submit')}</span>
            </button>
          </form>
        </div>
      </div>
    `;

    const close = () => {
      modal.remove();
      document.removeEventListener('keydown', onKeydown);
    };
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    modal.querySelector('#lead-modal-close')?.addEventListener('click', close);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', onKeydown);

    const form = modal.querySelector('#lead-form') as HTMLFormElement;
    const errorDiv = modal.querySelector('#lead-error') as HTMLElement;
    const submitBtn = modal.querySelector('#lead-submit') as HTMLButtonElement;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      errorDiv.classList.add('hidden');

      const name = (modal.querySelector('#lead-name') as HTMLInputElement).value.trim();
      const organizationName = (modal.querySelector('#lead-org') as HTMLInputElement).value.trim();
      const email = (modal.querySelector('#lead-email') as HTMLInputElement).value.trim();
      const phone = (modal.querySelector('#lead-phone') as HTMLInputElement).value.trim();
      const honeypot = (modal.querySelector('#organization_fax') as HTMLInputElement).value;

      if (!name || !organizationName || !email || !phone) {
        errorDiv.textContent = t('landing.lead.required');
        errorDiv.classList.remove('hidden');
        return;
      }

      submitBtn.disabled = true;
      try {
        await apiService.submitOrganizerLead({
          name,
          organizationName,
          email,
          phone,
          organization_fax: honeypot,
        });
        this.showLeadSuccess(modal, close);
      } catch (error) {
        console.error('[Landing] Lead submission failed:', error);
        errorDiv.textContent = error instanceof Error ? error.message : t('landing.lead.error');
        errorDiv.classList.remove('hidden');
        submitBtn.disabled = false;
      }
    });

    document.body.appendChild(modal);
    (modal.querySelector('#lead-name') as HTMLInputElement)?.focus();
  }

  /** Replace the lead form with a confirmation state after a successful submit. */
  private showLeadSuccess(modal: HTMLElement, close: () => void): void {
    const body = modal.querySelector('#lead-modal-body');
    if (!body) return;
    body.innerHTML = `
      <div class="text-center py-4">
        <div class="flex items-center justify-center size-12 bg-success/15 text-success rounded-full mx-auto mb-3">
          <span class="material-symbols-outlined text-3xl">check_circle</span>
        </div>
        <h2 class="text-xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${t('landing.lead.successTitle')}</h2>
        <p class="text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-1">${t('landing.lead.successBody')}</p>
        <button id="lead-success-done" class="btn-primary mt-5 w-full justify-center">${t('landing.lead.done')}</button>
      </div>
    `;
    body.querySelector('#lead-success-done')?.addEventListener('click', close);
  }

  private openDemoModal(): void {
    const modal = document.createElement('div');
    modal.className =
      'fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', t('landing.demo.ariaLabel'));
    modal.innerHTML = `
      <div class="relative w-full max-w-3xl bg-white dark:bg-card-dark daylight:bg-day-raised rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 p-4 sm:p-6 animate-in zoom-in-95 duration-200">
        <button id="demo-modal-close" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors" aria-label="${t('landing.demo.close')}">
          <span class="material-symbols-outlined">close</span>
        </button>
        <h2 class="text-xl font-bold text-slate-900 dark:text-white mb-4 pr-10">${t('landing.demo.title')}</h2>
        <div class="aspect-video w-full rounded-xl bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken border border-dashed border-slate-300 dark:border-slate-700 daylight:border-day-ink/20 flex flex-col items-center justify-center gap-3 text-center px-6">
          <span class="material-symbols-outlined text-5xl text-primary daylight:text-day-ink">play_circle</span>
          <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft text-sm font-medium">${t('landing.demo.comingSoon')}</p>
        </div>
      </div>
    `;

    const close = () => {
      modal.remove();
      document.removeEventListener('keydown', onKeydown);
    };
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    modal.querySelector('#demo-modal-close')?.addEventListener('click', close);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });
    document.addEventListener('keydown', onKeydown);

    document.body.appendChild(modal);
  }
}
